import React, { useState } from "react";
import { Avatar } from "@fluentui/react-components";
import { ExternalLink } from "lucide-react";
import Modal from "../../../components/controls/modal/Modal";
import HoverCard from "../../../components/hoverCard/HoverCard";
import styles from "./TagLibrary.module.css";

const PUBLIC_URL = process.env.PUBLIC_URL;

const HIERARCHY_SECTIONS = [
  { key: "clients", label: "Clients", route: "clients" },
  { key: "portfolios", label: "Portfolios", route: "portfolio" },
  { key: "programs", label: "Programs", route: "program" },
  { key: "projects", label: "Projects", route: "projects" },
];

const TagUsageModal = ({ mode, entries, affectedLabel, service, onCancel, onConfirmed }) => {
  const isRename = mode === "rename";
  const isUsers = affectedLabel === "Users";
  const [newName, setNewName] = useState(entries[0]?.item?.name || "");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const extractErrorMessage = (err, fallback) =>
    err?.data?.detail || err?.data?.Detail || fallback;

  const handleConfirm = () => {
    setError("");
    setIsLoading(true);

    if (isRename) {
      service
        .rename(entries[0].item.id, newName.trim())
        .then(() => onConfirmed())
        .catch((err) => setError(extractErrorMessage(err, "Rename failed.")))
        .finally(() => setIsLoading(false));
    } else {
      Promise.all(entries.map((entry) => service.remove(entry.item.id)))
        .then(() => onConfirmed())
        .catch((err) => setError(extractErrorMessage(err, "Delete failed.")))
        .finally(() => setIsLoading(false));
    }
  };

  return (
    <Modal
      title={isRename ? `Rename "${entries[0]?.item?.name}"` : "Delete tags"}
      handleCancel={onCancel}
      handleSave={handleConfirm}
      isLoading={isLoading}
      saveBtnLabel={isRename ? "Rename" : "Delete"}
      cancelBtnLabel="Cancel"
      isSaveBtnDisabled={isRename && !newName.trim()}
      body={
        <div className={styles.usageContainer}>
          {isRename && (
            <div className={styles.renameInputWrapper}>
              <label>New name</label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                autoFocus
              />
            </div>
          )}
          <p className={styles.usageHeader}>
            {isRename
              ? "Renaming this will change how it appears everywhere it's already assigned:"
              : "The following will be permanently removed. Anyone currently assigned will lose it:"}
          </p>
          {entries.map((entry) => {
            const sections = isUsers
              ? []
              : HIERARCHY_SECTIONS.map((section) => ({
                  ...section,
                  items: entry.usage?.[section.key] || [],
                })).filter((section) => section.items.length > 0);
            const users = isUsers ? entry.usage?.users || [] : [];
            const hasNoUsage = isUsers ? users.length === 0 : sections.length === 0;

            return (
              <div key={entry.item?.id} className={styles.usageEntry}>
                <span className={`pill secondary ${styles.tagPill}`}>
                  {entry.item?.name}
                </span>
                {hasNoUsage ? (
                  <p className={styles.noUsage}>
                    {isUsers
                      ? "Not currently assigned to any users."
                      : "Not currently assigned to any client, portfolio, program, or project."}
                  </p>
                ) : isUsers ? (
                  <ul className={styles.affectedList}>
                    {users.map((a) => (
                      <li key={a.id}>
                        <span className={`${styles.affectedPill} ${styles.hoverTrigger}`}>
                          <Avatar
                            size={20}
                            color="colorful"
                            name={a.name || a.email}
                            image={{ src: a.image }}
                          />
                          <span>{a.name || a.email}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  sections.map((section) => (
                    <div key={section.key} className={styles.usageSection}>
                      <p className={styles.usageSectionLabel}>{section.label}</p>
                      <ul className={styles.affectedList}>
                        {section.items.map((a) => (
                          <li key={a.id}>
                            <span className={styles.affectedPill}>
                              <span>{a.name}</span>
                              <button
                                type="button"
                                className={styles.linkButton}
                                title={`Open ${a.name}`}
                                onClick={() =>
                                  window.open(
                                    `${PUBLIC_URL}/${section.route}/${a.id}/overview`,
                                    "_blank"
                                  )
                                }
                              >
                                <ExternalLink size={14} />
                              </button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))
                )}
              </div>
            );
          })}
          {error && <p className={styles.errorMessage}>{error}</p>}
        </div>
      }
    />
  );
};

export default TagUsageModal;
